/**
 * BCM Project Development — Recharts chart components.
 *
 * Same export names / prop signatures as the earlier hand-SVG version, so the
 * consuming page (PdAnalyticsGraphs) is unchanged apart from the import path.
 * Recharts gives us hover tooltips, responsive resize and axis handling for
 * free. Pinned to recharts 2.x — recharts 3.x bundles react-redux@9, which
 * requires React 18's useSyncExternalStore and breaks under React 17.
 */
import React from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  BarChart as RBarChart,
  ScatterChart,
  AreaChart,
  Area,
  Bar,
  Line,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';

// Grid / axis / text keyed to the Project Development palette (#E0E0E0 borders,
// #666 secondary text, #333 body) so charts match the tables around them.
const GRID = '#E0E0E0';
const AXIS = '#666';
const INK = '#333';
// MUI's default typography stack — Recharts renders SVG <text>/portalled
// tooltips, so the font is stated rather than inherited.
const FONT = '"Roboto", "Helvetica", "Arial", sans-serif';
const axisTick = { fontSize: 11, fill: AXIS, fontFamily: FONT };
const xTick = { fontSize: 12, fill: INK, fontFamily: FONT };

const tooltipStyle = {
  contentStyle: { fontSize: 12, borderRadius: 4, border: '1px solid #E0E0E0', fontFamily: FONT },
  labelStyle: { fontWeight: 600, color: INK, fontFamily: FONT },
};

/* ── Plot 1 — combo: line (left Y) + bars (right Y) ────────────────────────── */
export const ComboChart = ({
  categories, line, bars, lineLabel = 'Line', barLabel = 'Bar',
  lineColor = '#3b82f6', barColor = '#64748b',
  leftMin = 50, leftMax = 100, height = 320,
}) => {
  const data = categories.map((c, i) => ({ batch: c, [lineLabel]: line[i], [barLabel]: bars[i] }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={data} margin={{ top: 12, right: 16, left: 0, bottom: 4 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey="batch" tick={xTick} tickLine={false} axisLine={{ stroke: GRID }} />
        <YAxis yAxisId="left" domain={[leftMin, leftMax]} tick={axisTick} tickLine={false} axisLine={false} width={42} />
        <YAxis yAxisId="right" orientation="right" tick={axisTick} tickLine={false} axisLine={false} width={44} />
        <Tooltip {...tooltipStyle} />
        <Bar yAxisId="right" dataKey={barLabel} fill={barColor} barSize={26} radius={[3, 3, 0, 0]} opacity={0.85} />
        <Line yAxisId="left" type="monotone" dataKey={lineLabel} stroke={lineColor} strokeWidth={2.5} dot={{ r: 3, strokeWidth: 1.5, stroke: '#fff' }} />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

/* ── Plot 2 — scatter with a dashed NMT reference line ─────────────────────── */
export const ScatterThreshold = ({ points, nmt, yMax, color = '#0d9488', height = 220 }) => {
  const data = points.map((p) => ({ batch: p.batch, value: p.value }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <ScatterChart margin={{ top: 14, right: 16, left: 0, bottom: 4 }}>
        <CartesianGrid stroke={GRID} />
        <XAxis dataKey="batch" type="category" allowDuplicatedCategory={false} tick={{ fontSize: 10, fill: INK }} tickLine={false} axisLine={{ stroke: GRID }} />
        <YAxis dataKey="value" domain={[0, yMax]} tick={axisTick} tickLine={false} axisLine={false} width={42} tickFormatter={(v) => v.toFixed(2)} />
        <Tooltip {...tooltipStyle} cursor={{ strokeDasharray: '3 3' }} />
        <ReferenceLine
          y={nmt}
          stroke={AXIS}
          strokeDasharray="6 4"
          strokeWidth={1.5}
          label={{ value: `NMT ${nmt}`, position: 'insideTopRight', fontSize: 10, fill: AXIS }}
        />
        <Scatter data={data} fill={color} fillOpacity={0.85} />
      </ScatterChart>
    </ResponsiveContainer>
  );
};

/* ── Plot 3a — single line ─────────────────────────────────────────────────── */
export const LineChart = ({
  categories, values, color = '#3b82f6', yMin, yMax, height = 260,
  // Axis titles — default to the purity plot's wording. Passed through so other
  // callers can label their own units.
  yLabel = 'Purity (%)', xLabel = 'Batch (b = 1 … N)',
}) => {
  const data = categories.map((c, i) => ({ batch: c, value: values[i] }));
  const axisLabel = { fontSize: 12, fill: INK, fontFamily: FONT };
  // Gradient id must be unique per colour, or two plots on the same page would
  // share one <defs> entry and the second would pick up the first's colour.
  const fillId = `lineFill-${String(color).replace('#', '')}`;
  return (
    <ResponsiveContainer width="100%" height={height}>
      {/* AreaChart (not LineChart) purely for the soft tint under the line —
          the export name stays LineChart so callers are unaffected.
          Extra bottom/left margin makes room for the axis titles. */}
      <AreaChart data={data} margin={{ top: 12, right: 16, left: 8, bottom: 24 }}>
        <defs>
          <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.3} />
            <stop offset="100%" stopColor={color} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis
          dataKey="batch"
          tick={xTick}
          tickLine={false}
          axisLine={{ stroke: GRID }}
          label={{ value: xLabel, position: 'insideBottom', offset: -16, style: axisLabel }}
        />
        <YAxis
          domain={[yMin != null ? yMin : 'auto', yMax != null ? yMax : 'auto']}
          tick={axisTick}
          tickLine={false}
          axisLine={false}
          width={54}
          label={{ value: yLabel, angle: -90, position: 'insideLeft', style: { ...axisLabel, textAnchor: 'middle' } }}
        />
        <Tooltip {...tooltipStyle} />
        <Area
          type="monotone"
          dataKey="value"
          stroke={color}
          strokeWidth={2.5}
          fill={`url(#${fillId})`}
          dot={{ r: 3, strokeWidth: 1.5, stroke: '#fff', fill: color }}
          activeDot={{ r: 4 }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

/* ── Plot 3b — grouped bars (two series) ───────────────────────────────────── */
export const GroupedBarChart = ({
  categories, seriesA, seriesB, seriesALabel = 'Series A', seriesBLabel = 'Series B',
  colorA = '#64748b', colorB = '#0d9488', height = 260,
}) => {
  const data = categories.map((c, i) => ({ batch: c, [seriesALabel]: seriesA[i], [seriesBLabel]: seriesB[i] }));
  return (
    <ResponsiveContainer width="100%" height={height}>
      <RBarChart data={data} margin={{ top: 12, right: 16, left: 0, bottom: 4 }} barGap={2}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey="batch" tick={xTick} tickLine={false} axisLine={{ stroke: GRID }} />
        <YAxis tick={axisTick} tickLine={false} axisLine={false} width={42} />
        <Tooltip {...tooltipStyle} />
        <Bar dataKey={seriesALabel} fill={colorA} barSize={13} radius={[2, 2, 0, 0]} opacity={0.9} />
        <Bar dataKey={seriesBLabel} fill={colorB} barSize={13} radius={[2, 2, 0, 0]} opacity={0.9} />
      </RBarChart>
    </ResponsiveContainer>
  );
};

/* ── shared swatch legend ──────────────────────────────────────────────────── */
// fontFamily is set explicitly: these are bare spans, and with no <CssBaseline />
// in the app they would otherwise inherit the browser's serif default.
export const Legend = ({ items }) => (
  <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', margin: '2px 0 10px', fontFamily: FONT }}>
    {items.map((it) => (
      <span key={it.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.875rem', color: '#333' }}>
        {/* 16px / 2px swatch — same as BatchGenealogy's legendSquare */}
        <span style={{ width: 16, height: 16, borderRadius: 2, background: it.color, display: 'inline-block' }} />
        {it.label}
      </span>
    ))}
  </div>
);
